<template>
  <ed-modal :title="'Create New ' + singularize(this.$root.getUser.settings.Labels.DivisionName)" :show="show"
    @close="$emit('close')" primary-button="Create" type="small-medium" validation @validate='addDivision'
    @confirm="addDivision">
    <div slot="body">
      <div class="form-group">
        <label for="name">{{ singularize(this.$root.getUser.settings.Labels.DivisionName) }} Name</label>
        <ed-input
          @input="(o) => { division.Name = o; divisionNameIncomplete = false; duplicateDivisionNameError = false; }"
          type="text" v-model="division.Name" id="divisionName" class="full-width"
          :error="divisionNameIncomplete || duplicateDivisionNameError" maxlength="100" />
        <div v-if="duplicateDivisionNameError" class="error-message">
          A division with this name already exists. Please choose a different name.
        </div>
      </div>
      <div class="form-group">
        <ed-textarea :field-label="singularize(this.$root.getUser.settings.Labels.DivisionName) + ' Description'"
          :rows="4" v-model="division.Description" :error="descriptionIncomplete" :required="false" :hideOptional="true"
          @input="(o) => {
            division.Description = o.length > 1000 ? o.substring(0, 1000) : o;
            descriptionIncomplete = false;
          }">
        </ed-textarea>
      </div>
      <p class="label-text">Products</p>
      <div class="form-group-checkbox">
        <input type="checkbox" id="EvedAP" v-model="division.AllowEvedAP">
        <label class="small-checkbox" for="EvedAP">Eved AP</label>
      </div>
      <div class="form-group-checkbox" v-if="this.$root.getUser.settings.HasEvedPayCards">
        <input type="checkbox" id="EvedPayCard" v-model="division.AllowEvedPayCards">
        <label class="small-checkbox" for="EvedPayCard">EvedPay Card</label>
      </div>
      <div class="form-group">
        <div class="form-group">
          <label>Entity</label>
          <div slot="additionalText" style="font-size: smaller; font-style: italic;">
            {{ `If the EIN is different for this ${singularize(this.$root.getUser.settings.Labels.DivisionName)}, please
            select New
            Entity and complete the section below` }}
          </div>
          <ed-select @input="handleEntitySelection" :options="[...entities]" @open="getEntity" v-model="selectEntity"
            label="Name" name="entityDropdown" track-by="Id" :error="entityIncomplete" />
        </div>
      </div>
      <div>
        <p class="bold">New Entity Set Up </p>
        <div slot="additionalText" style="font-size: smaller; font-style: italic;">
          Provide the EIN and Bill To address for the new entity
        </div>
      </div>

      <div class="vertical-line ">
        <div class="margin-left">
          <div class="form-group" :class="{ 'disabled': !isNewEntity }">
            <label for="name">Employer Identification Number (EIN)</label>
            <ed-input @input="(o) => { division.EIN = o; }" type="text" id="ein" class="full-width"
              v-model="division.EIN" :error="duplicateEINError || einIncomplete" maxlength="50" />
          </div>
          <div v-if="duplicateEINError" class="error-message">
            A EIN with this name already exists. Please choose a different name.
          </div>
          <div class="form-group" :class="{ 'disabled': !isNewEntity }">
            <ed-select field-label="Country" placeholder="Select a country..." :options="countries"
              v-model="formData.country" label="CountryName" track-by="CountryID"
              :openEndpoint='api.$.profile.getAllCountries' @select="countrySelected($event)"
              @open="fetchCountriesOnOpen" @keyup="fetchCountries" @close="restoreCountries" required name="country" />
          </div>
          <div class="form-group" :class="{ 'disabled': !isNewEntity }">
            <label for="name">Name</label>
            <ed-input @input="(o) => { mailingAddress.name = o; this.nameIncomplete = false; }" type="text"
              v-model="mailingAddress.name" id="name" :error="nameIncomplete" placeholder="Name" maxlength="40"
              class="full-width" />
          </div>

          <div class="form-group" :class="{ 'disabled': !isNewEntity }">
            <label for="address">Address</label>
            <ed-input @input="(o) => { mailingAddress.line1 = o; this.addressIncomplete = false; }" type="text"
              v-model="mailingAddress.line1" id="address" :error="addressIncomplete" placeholder="Address"
              maxlength="26" class="full-width" />
          </div>
          <div class="form-group" :class="{ 'disabled': !isNewEntity }">
            <ed-input @input="(o) => mailingAddress.line2 = o" type="text" field-label="Address Line 2"
              v-model="mailingAddress.line2" id="addressLine2" placeholder="Address Line 2" class="full-width" />
          </div>

          <div class="form-group" :class="{ 'disabled': !isNewEntity }">
            <div class="input-group">
              <!-- City -->
              <div class="input-group-item">
                <label for="city">City</label>
                <ed-input @input="(o) => { mailingAddress.city = o; this.cityIncomplete = false; }" type="text"
                  v-model="mailingAddress.city" id="city" :error="cityIncomplete" placeholder="City" maxlength="25"
                  class="full-width" />
              </div>

              <!-- State -->
              <div class="input-group-item margin-left">
                <ed-select @input="(o) => { mailingAddress.state = o; this.stateIncomplete = false; }" v-if="isUS()"
                  :options="USstates" v-model="formData.state" @open="fetchUSStatesOnOpen" @keyup="fetchUSStates"
                  @close="restoreUSStates" label="StateName" :error="stateIncomplete" track-by="StateID"
                  name="us_states" field-label="State" required />
                <ed-select @input="(o) => { mailingAddress.canadaState = o; this.canadaStateIncomplete = false; }"
                  v-if="isCanada()" placeholder="Select a region" :options="canadaStates" v-model="formData.canadaState"
                  @open="fetchCanadaStatesOnOpen" @keyup="fetchCanadaStates" @close="restoreCanadaStates"
                  label="StateName" :disabled="disableControls" :error="canadaStateIncomplete" name="canada_states" field-label="State/Region"
                  required />
                <ed-input @input="(o) => { mailingAddress.region = o; this.stateIncomplete = false; }"
                  v-if="!formData.country || (!isCanada() && !isUS())" placeholder="Select a region"
                  v-model="formData.region" :disabled="disableControls" name="state_region" field-label="State/Region"
                  required />
              </div>

              <!-- Zip Code -->
              <div class="input-group-item margin-left">
                <label for="zipCode">Zip Code</label>
                <ed-input @input="(o) => { mailingAddress.zip = o; this.zipCodeIncomplete = false; }" type="text"
                  v-model="mailingAddress.zip" id="zipCode" :error="zipCodeIncomplete" placeholder="Zip Code"
                  class="full-width" />
              </div>
            </div>
          </div>

        </div>
      </div>

      <div v-if="this.$root.getUser.settings.HasEvedPayCards && division.AllowEvedPayCards">
        <p class="bold">PEX Set Up </p>
        <div slot="additionalText" style="font-size: smaller; font-style: italic;">
          If a new entity is being created, please use this link to...
        </div>
        <div>
          <a href="https://start.pexcard.com/get-started?partnersOfPex=Eved" target="_blank" class="link-with-icon">
            Set Up Entity with PEX
          </a>
          <i class="fa fa-share-square custom-icon-color"></i>
        </div>
      </div>

    </div>
  </ed-modal>
</template>

<script>
import { singularize } from '@/core/utils/string.js'

export default {
  props: {
    show: {
      type: Boolean,
      required: true
    }
  },
  data: function () {
    return {
      formData: {
        city: '',
        postalCode: '',
        originalCountryName: '',
        country: '',
        state: {
          StateID: 0,
          StateName: ''
        },
        canadaState: {
          StateID: 0,
          StateName: ''
        },
        region: ''
      },
      division: {
        Name: '',
        Description: '',
        AllowEvedAP: false,
        AllowEvedPayCards: false,
        DivisionID: '',
        EIN: ''
      },

      USstates: [],
      canadaStates: [],
      entities: [],
      selectEntity: '',
      countries: [],
      showAddDivision: false,
      mailingAddress: {},
      divisionNameIncomplete: false,
      einIncomplete: false,
      descriptionIncomplete: false,
      countryIncomplete: false,
      addressIncomplete: false,
      cityIncomplete: false,
      stateIncomplete: false,
      canadaStateIncomplete: false,
      zipCodeIncomplete: false,
      entityIncomplete: false,
      disableControls: false,
      isNewEntity: false,
      duplicateDivisionNameError: false,
      duplicateEINError: false
    }
  },
  methods: {
    singularize(word) {
      return singularize(word)
    },
    async getEntity() {
      try {
        const vendorId = this.$root.getUser.settings.VendorId

        const response = await this.api.get(this.api.$.settings.company.getEntity, {
          params: { vendorId }
        })

        if (response && response.data && response.data.EINs) {
          this.entities = [
            { Id: 'new', Name: 'Add New Entity' },
            ...response.data.EINs.map(ein => ({
              Id: ein.VendorAddressId,
              Name: ein.Name,
              Address: ein.Address,
              Address2: ein.Address2,
              City: ein.City,
              EIN: ein.EIN,
              Zip: ein.Zip,
              StateId: ein.StateId,
              StateName: ein.StateName,
              CountryId: ein.CountryId,
              Country: ein.Country
            }))
          ]
        }
      } catch (error) {
        console.error('Error fetching EINs:', error)
      }
    },
    handleEntitySelection(value) {
      this.isNewEntity = value.Id === 'new'

      if (!this.isNewEntity) {
        this.duplicateEINError = false
        this.countryIncomplete = false
        this.cityIncomplete = false
        this.zipCodeIncomplete = false
        this.stateIncomplete = false
        this.canadaStateIncomplete = false
        this.nameIncomplete = false
        this.entityIncomplete = false

        const selectedEntity = this.entities.find(entity => entity.Id === value.Id)

        if (selectedEntity) {
          this.division.EIN = selectedEntity.EIN
          this.mailingAddress = {
            name: selectedEntity.Name,
            line1: selectedEntity.Address,
            line2: selectedEntity.Address2,
            city: selectedEntity.City,
            state: selectedEntity.StateName,
            zip: selectedEntity.Zip,
            country: selectedEntity.Country,
            region: selectedEntity.Region
          }

          this.formData = {
            country: this.countries.find(c => c.CountryID === selectedEntity.CountryId) || { CountryID: selectedEntity.CountryId, CountryName: selectedEntity.Country },
            state: this.USstates.find(s => s.StateID === selectedEntity.StateId) || { StateID: selectedEntity.StateId, StateName: selectedEntity.StateName },
            canadaState: this.canadaStates.find(s => s.StateID === selectedEntity.StateId) || { StateID: selectedEntity.StateId, StateName: selectedEntity.StateName }
          }

          this.disableControls = true
        }
      } else {
        this.division.EIN = ''
        this.mailingAddress = {
          name: '',
          line1: '',
          line2: '',
          city: '',
          state: '',
          zip: ''
        }
        this.formData = {
          country: '',
          state: { StateID: 0, StateName: '' },
          canadaState: { StateID: 0, StateName: '' },
          region: ''
        }
        this.disableControls = false
        this.duplicateEINError = false
        this.einIncomplete = false
      }

      if (value && value.Id) {
        this.entityIncomplete = false
      }
    },
    async addDivision() {
      let valid = true

      if (!this.division.Name) {
        this.divisionNameIncomplete = true
        valid = false
      } else {
        this.divisionNameIncomplete = false
      }

      if (!this.division.Description) {
        this.descriptionIncomplete = true
        valid = false
      } else {
        this.descriptionIncomplete = false
      }

      const isDuplicate = await this.checkDivisionNameExists(this.division.Name)
      if (isDuplicate) {
        this.duplicateDivisionNameError = true
        return
      } else {
        this.duplicateDivisionNameError = false
      }

      if (this.isNewEntity) {
        const isEINDuplicate = await this.checkEINExists(this.division.EIN)
        if (isEINDuplicate) {
          this.duplicateEINError = true
          return
        } else {
          this.duplicateEINError = false
        }
      }

      if (this.isNewEntity && !this.mailingAddress.name) {
        this.nameIncomplete = true
        valid = false
      } else {
        this.nameIncomplete = false
      }

      if (this.isNewEntity && !this.division.EIN.trim()) {
        this.einIncomplete = true
        valid = false
      } else {
        this.einIncomplete = false
      }

      if (this.isNewEntity && !this.mailingAddress.city) {
        this.cityIncomplete = true
        valid = false
      } else {
        this.cityIncomplete = false
      }

      if (this.isNewEntity && !this.mailingAddress.zip) {
        this.zipCodeIncomplete = true
        valid = false
      } else {
        this.zipCodeIncomplete = false
      }

      if (this.isNewEntity && !this.mailingAddress.line1) {
        this.addressIncomplete = true
        valid = false
      } else {
        this.addressIncomplete = false
      }

      if (this.isNewEntity) {
        if (this.isUS() && !this.mailingAddress.state) {
          this.stateIncomplete = true
          valid = false
        } else {
          this.stateIncomplete = false
        }
      }

      if (this.isNewEntity) {
        if (this.isCanada() && !this.mailingAddress.canadaState) {
          this.canadaStateIncomplete = true
          valid = false
        } else {
          this.canadaStateIncomplete = false
        }
      }

      if (!this.isNewEntity && (!this.selectEntity || !this.selectEntity.Id)) {
        this.entityIncomplete = true
        valid = false
      } else {
        this.entityIncomplete = false
      }

      if (!valid) {
        return
      }

      try {
        const payload = {
          DivisionName: this.division.Name,
          DivisionDescription: this.division.Description,
          DivisionID: this.division.DivisionID || 0,
          AllowEvedAP: this.division.AllowEvedAP || false,
          AllowEvedPayCards: this.division.AllowEvedPayCards || false,
          EIN: this.division.EIN,
          IsNewEntity: this.isNewEntity,
          Name: this.mailingAddress.name || '',
          VendorAddressId: this.isNewEntity ? null : (this.selectEntity ? this.selectEntity.Id : null),
          Address: this.mailingAddress.line1 || '',
          Address2: this.mailingAddress.line2 || '',
          City: this.mailingAddress.city || '',
          CountryID: this.formData.country.CountryID || null,
          StateID: this.isUS() ? this.formData.state.StateID : (this.isCanada() ? (this.formData.canadaState.StateID || null) : null),
          Region: this.isCanada() ? this.formData.canadaState.StateName : (!this.isUS() ? this.formData.region : ''),
          Zip: this.mailingAddress.zip || ''
        }

        const response = await this.api.post(this.api.$.settings.company.addDivision, payload)

        if (response && response.data) {
          this.$emit('close')
        } else {
          console.error('Failed to add division')
        }
      } catch (error) {
        console.error('Error adding division:', error)
      }
    },
    async checkDivisionNameExists(divisionName) {
      try {
        const response = await this.api.get(this.api.$.settings.company.checkDivisionNameUnique, {
          params: { divisionName }
        })

        return response.data.SuccessMessage === 'NotUnique'
      } catch (error) {
        console.error('Error checking division name:', error)
        return false
      }
    },
    async checkEINExists(ein) {
      try {
        const response = await this.api.get(this.api.$.settings.company.checkEINUnique, {
          params: { ein }
        })

        return response.data.SuccessMessage === 'NotUnique'
      } catch (error) {
        console.error('Error checking ein:', error)
        return false
      }
    },
    fetchCountriesOnOpen() {
      if (this.countries.length) {
      } else {
        this.api.forms.post(
          this.api.$.profile.getAllCountries,
          {
            q: ''
          }
        ).then(r => {
          if (r && r.data && r.data.Data) {
            this.countries = r.data.Data
            this.originalCountries = r.data.Data
          }
        }
        ).catch(e => console.warn(e))
      }
    },
    fetchCountries(value) {
      this.counter || this.api.forms.post(
        this.api.$.profile.getAllCountries,
        {
          q: value || ''
        }
      ).then(r => {
        this.counter = 0
        if (r && r.data && r.data.Data) {
          this.countries = r.data.Data
        }
      }
      ).catch(e => {
        console.warn(e)
        this.counter = 0
      })
      this.counter = 1
    },
    restoreCountries() {
      if (this.counter === 0) {
        this.countries = utils.copy(this.originalCountries)
      }
    },
    isUS() {
      return this.formData.country && this.formData.country.CountryName === 'United States'
    },
    GetStatesUSURL() {
      return this.api.$.profile.getAllStates
    },

    isCanada() {
      return this.formData.country && this.formData.country.CountryName === 'Canada'
    },
    GetStatesCanadaURL() {
      return this.api.$.profile.getCanadaStates
    },

    // Fetch States on OPEN
    fetchUSStatesOnOpen() {
      var url = this.GetStatesUSURL()
      this.fetchStatesOnOpen(this.USstates, url)
    },
    fetchCanadaStatesOnOpen() {
      var url = this.GetStatesCanadaURL()
      this.fetchStatesOnOpen(this.canadaStates, url)
    },
    fetchStatesOnOpen(states, url) {
      if (states.length && (this.isCanada() || this.isUS())) {
      } else {
        this.api.forms.post(
          url,
          {
            q: ''
          }
        ).then(r => {
          if (r && r.data && r.data.Data) {
            this.mapStatesDataOnOpen(r.data.Data)
          }
        }
        ).catch(e => console.warn(e))
      }
    },
    mapStatesDataOnOpen(data) {
      if (this.isUS()) {
        this.USstates = this.USoriginalStates = data
      } else {
        this.canadaStates = this.originalCanadaStates = data
      }
    },

    // Fetch States on search text
    fetchUSStates(value) {
      var url = this.GetStatesUSURL()
      this.fetchStates(value, url)
    },
    fetchCanadaStates(value) {
      var url = this.GetStatesCanadaURL()
      this.fetchStates(value, url)
    },
    fetchStates(value, url) {
      this.counter || this.api.forms.post(
        url,
        {
          q: value || ''
        }
      ).then(r => {
        this.counter = 0
        if (r && r.data && r.data.Data) {
          this.mapStatesDataOnFetch(r.data.Data)
        }
      }
      ).catch(e => {
        console.warn(e)
        this.counter = 0
      })
      this.counter = 1
    },
    mapStatesDataOnFetch(data) {
      if (this.isUS()) {
        this.USstates = data
      } else {
        this.canadaStates = data
      }
    },

    // Restore States
    restoreUSStates() {
      this.restoreStates(this.USstates, this.USoriginalStates)
    },
    restoreCanadaStates() {
      this.restoreStates(this.canadaStates, this.originalCanadaStates)
    },
    restoreStates(states, originalStates) {
      if (this.counter === 0) {
        this.states = utils.copy(originalStates)
      }
    },
    populateFormData(data) {
      if (data.CompanyName !== null) {
        this.formData = {
          city: data.City,
          postalCode: data.Zip,
          originalCountryName: data.CountryName,
          country: {
            'CountryID': 186,
            'CountryName': 'United States'
          },
          state: {
            StateID: data.isCanada === 'False' && data.StateID || 0,
            StateName: data.isCanada === 'False' && data.State || ''
          },
          canadaState: {
            StateID: data.isCanada === 'True' && data.StateID || 0,
            StateName: data.isCanada === 'True' && data.State || ''
          },
          region: data.Region
        }
        this.disableControls = true
      } else {
        this.disableControls = false
      }
    },
    countrySelected(e) {
      this.doDisplayCountryError = false
      this.resetState(e.CountryName)
    },

    resetState(countryName) {
      if (this.originalCountryName !== '' && this.originalCountryName !== countryName) {
        this.formData.state = {
          StateID: 0,
          StateName: ''
        }
        this.formData.canadaState = {
          StateID: 0,
          StateName: ''
        }
        this.formData.region = ''
      } else {
        this.originalCountryName = countryName
      }
    },

    showModal(showAddDivision) {
      this.showAddDivision = !showAddDivision
      if (!this.showAddDivision) {
        this.resetModalFields()
      }
    },
    resetModalFields() {
      this.$nextTick(() => {
        this.division = {
          Name: '',
          Description: '',
          AllowEvedAP: false,
          AllowEvedPayCards: false,
          DivisionID: '',
          EIN: ''
        }

        this.formData = {
          city: '',
          postalCode: '',
          originalCountryName: '',
          country: '',
          state: { StateID: 0, StateName: '' },
          canadaState: { StateID: 0, StateName: '' },
          region: ''
        }

        this.mailingAddress = {
          name: '',
          line1: '',
          line2: '',
          city: '',
          state: '',
          zip: '',
          country: ''
        }

        this.isNewEntity = false
        this.mailingAddress = {}
        this.selectEntity = ''
        this.countryIncomplete = false
        this.divisionNameIncomplete = false
        this.descriptionIncomplete = false
        this.addressIncomplete = false
        this.cityIncomplete = false
        this.stateIncomplete = false
        this.canadaStateIncomplete = false
        this.zipCodeIncomplete = false
        this.disableControls = false
        this.nameIncomplete = false
        this.selectEntity = false
        this.entityIncomplete = false
        this.einIncomplete = false
        this.duplicateEINError = false
      })
    }
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.resetModalFields()
      } else {
        this.populateFormData(this.formData)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: 10px;
}

.form-group-checkbox {
  display: flex;
}

.small-checkbox {
  margin-left: 0.5rem;
  padding-top: 8px
}

.bold {
  font-size: medium;
  font-weight: bold;
  margin-bottom: 0px;
}

.vertical-line {
  border-left: 3px solid #dcdcdc;
  height: 100%;
  left: 0;
}

.margin-left {
  margin-left: 10px;
}

.input-group {
  display: flex;
  flex-wrap: wrap;
}

.input-group-item {
  flex: 1;
  margin-right: 10px;
}

.link-with-icon {
  color: #007bff;
  text-decoration: underline;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.custom-icon-color {
  color: #007bff;
  font-size: 16px;
}

.label-text {
  font-weight: 600;
  font-size: 0.85rem;
}

.error {
  color: red;
}

.error-message {
  color: red;
  font-size: 0.8rem;
}

.disabled {
  opacity: 0.6;
  pointer-events: none;
}


.ed-select .dropdown-item[data-id="new"] {
  background-color: #007bff;
  color: #fff;
  text-align: center;
  font-weight: bold;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  cursor: pointer;
}

.ed-select .dropdown-item[data-id="new"]:hover {
  background-color: #0056b3;
}
</style>



// WEBPACK FOOTER //
// src/components/_modules/Dashboard/widgets/Shortcuts/addDivisionModal.vue